import React, { useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Modal, Button, Dropdown } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import {
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

import PageTitle from "../../layouts/PageTitle";
import ExcelDownloadButton from "../../components/ExcelDownloadButton";
import AllotImprestModal from "../../components/AllotImprestModal";
import {
  appApi,
  useGetExpensesQuery,
  useGetExpensesForChartQuery,
  useGetFinanceCategoriesQuery,
  useGetVehiclesQuery,
} from "../../../store/appApi";
import { updateExpense } from "../../../services/expenseService";
import {
  selectUser,
  selectUserRole,
  selectHasModuleAccess,
} from "../../../store/authSlice";

// ─── 24-hour edit window helper ───────────────────────────────────────────────
const isEditWindowExpired = (createdAt) => {
  const ageMs = Date.now() - new Date(createdAt).getTime();
  return ageMs > 24 * 60 * 60 * 1000;
};

// ─── Chart constants ──────────────────────────────────────────────────────────
const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];
// YEAR_OPTIONS is now derived dynamically from real entries (see below)
const C_INCOME = "#10b981"; // emerald — income / receipts
const C_EXPENSE = "#e11d48"; // rose    — expenses
const C_NET = "#324336"; // indigo  — net position

const fmtINR = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");
const sumArr = (arr) => arr.reduce((a, b) => a + b, 0);

// ─── Info column badge — one consistent pill shape/size for every variant ────
const INFO_BADGE_COLORS = {
  vehicle: { background: "#e0f2fe", color: "#0369a1" },
  contractor: { background: "#dcfce7", color: "#15803d" },
  supplier: { background: "#f1f5f9", color: "#475569" },
  imprest: { background: "#fef3c7", color: "#92400e" },
  netplus: { background: "#ede9fe", color: "#6d28d9" },
};

const InfoBadge = ({ variant, icon, children }) => (
  <span
    className="d-inline-flex align-items-center gap-1"
    style={{
      ...INFO_BADGE_COLORS[variant],
      padding: "3px 10px",
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 600,
      whiteSpace: "nowrap",
    }}
  >
    {icon && <i className={`fa ${icon}`} />}
    {children}
  </span>
);

// ─── Action column detail button — same compact shape for every variant ──────
const DetailButton = ({ variant, icon, title, onClick, children }) => (
  <button
    type="button"
    className={`btn btn-xs btn-outline-${variant} d-inline-flex align-items-center gap-1`}
    title={title}
    onClick={onClick}
  >
    <i className={`fa ${icon}`} />
    {children}
  </button>
);

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e9ecef",
        borderRadius: 6,
        padding: "10px 14px",
        fontSize: 12,
      }}
    >
      <p style={{ margin: "0 0 6px", fontWeight: 600, color: "#495057" }}>
        {label}
      </p>
      {payload.map((entry) => (
        <p key={entry.name} style={{ margin: "2px 0", color: entry.color }}>
          {entry.name}: <strong>{fmtINR(entry.value)}</strong>
        </p>
      ))}
    </div>
  );
};

// ─── ExpenseList ──────────────────────────────────────────────────────────────
const ExpenseList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentUser = useSelector(selectUser);
  const hasFinanceWrite = useSelector(
    selectHasModuleAccess("finance", "read-write"),
  );
  const isSuperAdmin = useSelector(selectUserRole) === "super-admin";

  // ── Chart state ───────────────────────────────────────────────────────────
  const [chartYear, setChartYear] = useState(new Date().getFullYear());
  const [trendYear, setTrendYear] = useState(new Date().getFullYear());

  const { data: chartRawData } = useGetExpensesForChartQuery();
  const allEntries = Array.isArray(chartRawData?.finance)
    ? chartRawData.finance
    : [];

  const YEAR_OPTIONS = useMemo(() => {
    const currentYear = new Date().getFullYear();
    if (!allEntries.length) return [currentYear];
    const years = new Set(
      allEntries.map((e) => new Date(e.created).getFullYear()),
    );
    years.add(currentYear);
    return [...years].sort((a, b) => a - b);
  }, [allEntries]);

  const { monthlyData, totalIncome, totalExpense } = useMemo(() => {
    const incomeB = Array(12).fill(0);
    const expenseB = Array(12).fill(0);
    allEntries.forEach((entry) => {
      const d = new Date(entry.created);
      if (d.getFullYear() === chartYear) {
        if (entry.type === "receipt")
          incomeB[d.getMonth()] += Number(entry.amount || 0);
        else expenseB[d.getMonth()] += Number(entry.amount || 0);
      }
    });
    return {
      monthlyData: MONTHS.map((label, i) => ({
        label,
        Income: incomeB[i],
        Expense: expenseB[i],
      })),
      totalIncome: sumArr(incomeB),
      totalExpense: sumArr(expenseB),
    };
  }, [allEntries, chartYear]);

  const { trendData, trendHasData } = useMemo(() => {
    const incomeB = Array(12).fill(0);
    const expenseB = Array(12).fill(0);
    allEntries.forEach((entry) => {
      const d = new Date(entry.created);
      if (d.getFullYear() === trendYear) {
        if (entry.type === "receipt")
          incomeB[d.getMonth()] += Number(entry.amount || 0);
        else expenseB[d.getMonth()] += Number(entry.amount || 0);
      }
    });
    const hasData = incomeB.some((v) => v > 0) || expenseB.some((v) => v > 0);
    const lastFilledIdx = incomeB.reduce(
      (last, v, i) => (v > 0 || expenseB[i] > 0 ? i : last),
      -1,
    );
    const visibleCount =
      lastFilledIdx === 11 ? 12 : Math.min(lastFilledIdx + 2, 12);
    const slicedMonths = hasData ? MONTHS.slice(0, visibleCount) : MONTHS;

    let running = 0;
    return {
      trendData: slicedMonths.map((label, i) => {
        running += incomeB[i] - expenseB[i];
        return { label, net: running || null };
      }),
      trendHasData: hasData,
    };
  }, [allEntries, trendYear]);

  const netBalance = totalIncome - totalExpense;

  // Actual current Daybook balance (all-time, not scoped to the chart's
  // selected year) — the most recent entry's chained `balance` field.
  const currentDaybookBalance = useMemo(() => {
    if (!allEntries.length) return 0;
    const latest = [...allEntries].sort(
      (a, b) => new Date(b.created) - new Date(a.created),
    )[0];
    return Number(latest?.balance) || 0;
  }, [allEntries]);

  // ── Pagination & filter state ─────────────────────────────────────────────
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState(20);
  const [filterCategory, setFilterCategory] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");

  // ── Edit modal state ──────────────────────────────────────────────────────
  const [editModal, setEditModal] = useState({ show: false, record: null });
  const [editForm, setEditForm] = useState({});
  const [editErrors, setEditErrors] = useState({});
  const [editLoading, setEditLoading] = useState(false);
  const [lockModal, setLockModal] = useState({ show: false, record: null });

  // ── Transfer to Employee modal state ──────────────────────────────────────
  const [showAllotModal, setShowAllotModal] = useState(false);

  // ── Remote data ───────────────────────────────────────────────────────────
  const {
    data: expenseData,
    isLoading,
    isFetching,
  } = useGetExpensesQuery({
    page,
    categoryId: filterCategory,
    dateFrom: filterDateFrom,
    dateTo: filterDateTo,
  });

  const { data: catData } = useGetFinanceCategoriesQuery();
  const categories = Array.isArray(catData?.list) ? catData.list : [];

  const { data: vehicleData } = useGetVehiclesQuery();
  const vehicles = Array.isArray(vehicleData?.data) ? vehicleData.data : [];

  const expenses = Array.isArray(expenseData?.finance)
    ? expenseData.finance
    : [];
  const total = Number(expenseData?.total) || 0;
  const totalPages = Math.max(1, Math.ceil(total / sort));
  const showLoading = isLoading || isFetching;

  const balanceMap = useMemo(() => {
    if (!expenses.length) return {};
    const sorted = [...expenses].sort(
      (a, b) => new Date(a.created) - new Date(b.created),
    );
    let running = 0;
    const map = {};
    sorted.forEach((entry) => {
      if (entry.type === "receipt") running += Number(entry.amount);
      else running -= Number(entry.amount);
      map[entry._id] = running;
    });
    return map;
  }, [expenses]);

  const handleEditClick = (expense) => {
    const expired = isEditWindowExpired(expense.created);

    if (expired && !hasFinanceWrite) {
      setLockModal({ show: true, record: expense });
      return;
    }
    setEditForm({
      amount: expense.amount ?? "",
      categoryId: expense.category?._id || expense.categoryId || "",
      desc: expense.desc || "",
      isVehicle: expense.isVehicleExpense || false,
      vehicleId: expense.vehicleId || "",
    });
    setEditErrors({});
    setEditModal({ show: true, record: expense });
  };

  const handleEditChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === "checkbox" ? checked : value;
    setEditForm((prev) => ({
      ...prev,
      [name]: newValue,
      ...(name === "isVehicle" && !checked ? { vehicleId: "" } : {}),
    }));
    setEditErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateEdit = () => {
    const e = {};
    if (!editForm.amount || Number(editForm.amount) <= 0)
      e.amount = "Enter a valid positive amount.";
    if (!editForm.categoryId) e.categoryId = "Category is required.";
    if (editForm.isVehicle && !editForm.vehicleId)
      e.vehicleId = "Select a vehicle.";
    return e;
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateEdit();
    if (Object.keys(validationErrors).length) {
      setEditErrors(validationErrors);
      return;
    }
    try {
      setEditLoading(true);
      const payload = {
        amount: Number(editForm.amount),
        categoryId: editForm.categoryId,
        // paidBy is not editable here (the list only returns the payer's name,
        // not an id), so it is left unchanged.
        desc: editForm.desc,
        isVehicle: editForm.isVehicle,
        ...(editForm.isVehicle ? { vehicleId: editForm.vehicleId } : {}),
      };
      await updateExpense(editModal.record._id, payload);
      dispatch(appApi.util.invalidateTags(["Expenses"]));
      setEditModal({ show: false, record: null });
      Swal.fire({
        icon: "success",
        title: "Entry Updated",
        text: "Finance entry updated successfully.",
        timer: 1800,
        showConfirmButton: false,
      });
    } catch (err) {
      const msg = err?.response?.data?.message || "Failed to update entry.";
      Swal.fire({ icon: "error", title: "Error", text: msg });
    } finally {
      setEditLoading(false);
    }
  };

  const showingFrom = total > 0 ? (page - 1) * sort + 1 : 0;
  const showingTo = total > 0 ? Math.min(page * sort, total) : 0;
  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);
  const formatDate = (d) =>
    d ? new Date(d).toLocaleDateString("en-IN", { dateStyle: "medium" }) : "-";
  const badgeClass = (type) =>
    type === "receipt" ? "badge badge-success" : "badge badge-danger";
  const isValidId = (id) => /^[a-fA-F0-9]{24}$/.test(String(id ?? ""));

  return (
    <>
      <PageTitle activeMenu="Expenses" motherMenu="Finance" />

      {/* ══ CHARTS ROW ══════════════════════════════════════════════════════════ */}
      <div className="row">
        {/* Chart 1 — Income vs Expense by Month */}
        <div className="col-lg-6 d-flex flex-column">
          <div className="card mb-4 h-100">
            <div
              className="card-header d-flex flex-wrap align-items-center gap-3 py-3"
              style={{ background: "#fff", borderBottom: "1px solid #f0f2f5" }}
            >
              <div>
                <h6 className="mb-0 fw-semibold">Income vs Expense</h6>
                <small className="text-muted">
                  Monthly breakdown · receipts and payments
                </small>
              </div>
              <div className="d-flex align-items-center gap-2 ms-auto">
                <label className="mb-0 text-muted small fw-semibold">
                  Year
                </label>
                <select
                  className="form-select form-select-sm"
                  style={{ width: 88 }}
                  value={chartYear}
                  onChange={(e) => setChartYear(Number(e.target.value))}
                >
                  {YEAR_OPTIONS.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* KPI strip */}
            <div
              className="d-flex flex-wrap gap-0"
              style={{ borderBottom: "1px solid #f0f2f5" }}
            >
              <div
                className="flex-fill px-4 py-3"
                style={{
                  borderRight: "1px solid #f0f2f5",
                  borderLeft: `4px solid ${C_INCOME}`,
                }}
              >
                <span className="d-block text-muted small mb-1">
                  Total Income
                </span>
                <span className="fs-5 fw-bold" style={{ color: C_INCOME }}>
                  {fmtINR(totalIncome)}
                </span>
              </div>
              <div
                className="flex-fill px-4 py-3"
                style={{
                  borderRight: "1px solid #f0f2f5",
                  borderLeft: `4px solid ${C_EXPENSE}`,
                }}
              >
                <span className="d-block text-muted small mb-1">
                  Total Expense
                </span>
                <span className="fs-5 fw-bold" style={{ color: C_EXPENSE }}>
                  {fmtINR(totalExpense)}
                </span>
              </div>
              <div
                className="flex-fill px-4 py-3"
                style={{
                  borderLeft: `4px solid ${netBalance >= 0 ? C_INCOME : C_EXPENSE}`,
                }}
              >
                <span className="d-block text-muted small mb-1">Net</span>
                <span
                  className="fs-5 fw-bold"
                  style={{ color: netBalance >= 0 ? C_INCOME : C_EXPENSE }}
                >
                  {netBalance >= 0 ? "+" : ""}
                  {fmtINR(netBalance)}
                </span>
              </div>
            </div>

            <div className="card-body px-3 pt-3 pb-2">
              <ResponsiveContainer width="100%" height={280}>
                <BarChart
                  data={monthlyData}
                  margin={{ top: 8, right: 16, left: 8, bottom: 4 }}
                  barCategoryGap="28%"
                  barGap={4}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                    stroke="#f0f2f5"
                  />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 12, fill: "#6c757d" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tickFormatter={fmtINR}
                    tick={{ fontSize: 11, fill: "#6c757d" }}
                    axisLine={false}
                    tickLine={false}
                    width={90}
                  />
                  <Tooltip
                    content={<ChartTooltip />}
                    cursor={{ fill: "#f8f9fa" }}
                  />
                  <Legend
                    wrapperStyle={{ fontSize: 13, paddingTop: 12 }}
                    iconType="circle"
                    iconSize={10}
                  />
                  <Bar
                    dataKey="Income"
                    fill={C_INCOME}
                    radius={[4, 4, 0, 0]}
                    maxBarSize={48}
                  />
                  <Bar
                    dataKey="Expense"
                    fill={C_EXPENSE}
                    radius={[4, 4, 0, 0]}
                    maxBarSize={48}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Chart 2 — Cumulative Net Position */}
        <div className="col-lg-6 d-flex flex-column">
          <div className="card mb-4 h-100 d-flex flex-column">
            <div
              className="card-header d-flex flex-wrap align-items-center gap-3 py-3"
              style={{ background: "#fff", borderBottom: "1px solid #f0f2f5" }}
            >
              <div>
                <h6 className="mb-0 fw-semibold">Cumulative Net Position</h6>
                <small className="text-muted">
                  Running fund balance · month by month
                </small>
              </div>
              <div className="d-flex align-items-center gap-2 ms-auto">
                <label className="mb-0 text-muted small fw-semibold">
                  Year
                </label>
                <select
                  className="form-select form-select-sm"
                  style={{ width: 88 }}
                  value={trendYear}
                  onChange={(e) => setTrendYear(Number(e.target.value))}
                >
                  {YEAR_OPTIONS.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="card-body px-3 pt-3 pb-2 flex-grow-1 d-flex flex-column">
              <div style={{ flex: 1, minHeight: 0 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart
                    data={trendData}
                    margin={{ top: 8, right: 16, left: 8, bottom: 4 }}
                  >
                    <defs>
                      <linearGradient
                        id="financeNetGrad"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="5%"
                          stopColor={C_NET}
                          stopOpacity={0.25}
                        />
                        <stop
                          offset="95%"
                          stopColor={C_NET}
                          stopOpacity={0.02}
                        />
                      </linearGradient>
                    </defs>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      vertical={false}
                      stroke="#f0f2f5"
                    />
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 12, fill: "#6c757d" }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      tickFormatter={fmtINR}
                      tick={{ fontSize: 11, fill: "#6c757d" }}
                      axisLine={false}
                      tickLine={false}
                      width={90}
                    />
                    <Tooltip
                      content={<ChartTooltip />}
                      cursor={{
                        stroke: C_NET,
                        strokeWidth: 1,
                        strokeDasharray: "4 4",
                      }}
                    />
                    <Legend
                      wrapperStyle={{ fontSize: 13, paddingTop: 12 }}
                      iconType="circle"
                      iconSize={10}
                    />
                    <Area
                      type="monotone"
                      dataKey="net"
                      name={`Net Position ${trendYear}`}
                      stroke={C_NET}
                      strokeWidth={2.5}
                      fill="url(#financeNetGrad)"
                      dot={{ r: 4, fill: C_NET, strokeWidth: 0 }}
                      activeDot={{ r: 6, fill: C_NET }}
                      connectNulls={false}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ══ EXPENSE TABLE ════════════════════════════════════════════════════════ */}
      <div className="row">
        <div className="col-lg-12">
          <div className="card">
            <div className="card-header d-flex flex-wrap gap-2 align-items-center">
              <h4 className="card-title mb-0">All Finance Entries</h4>
              <div className="ms-auto d-flex gap-2 align-items-center">
                <ExcelDownloadButton
                  endpoint="/api/finance/entries/export"
                  params={{
                    categoryId: filterCategory,
                    dateFrom: filterDateFrom,
                    dateTo: filterDateTo,
                  }}
                  title={(() => {
                    const cat = categories.find(
                      (c) => c._id === filterCategory,
                    );
                    return cat
                      ? `Finance Entries — ${cat.categoryName}`
                      : "Finance Entries";
                  })()}
                  filename="finance-entries.xlsx"
                  label="Export Excel"
                />
                {hasFinanceWrite && (
                  <Link
                    to="/payments/add/expense"
                    className="btn btn-primary btn-sm"
                  >
                    + Add Entry
                  </Link>
                )}
                {isSuperAdmin && (
                  <button
                    type="button"
                    className="btn btn-outline-dark btn-sm"
                    onClick={() => setShowAllotModal(true)}
                    disabled={currentDaybookBalance <= 0}
                    title={
                      currentDaybookBalance <= 0
                        ? "No Daybook balance available to transfer"
                        : undefined
                    }
                  >
                    Transfer to Employee
                  </button>
                )}
              </div>
            </div>

            {/* Filters */}
            <div className="card-body border-bottom pb-3">
              <div className="row g-2 align-items-end">
                <div className="col-sm-4 col-md-3">
                  <label className="form-label mb-1 small">Category</label>
                  <select
                    className="form-control form-control-sm"
                    value={filterCategory}
                    onChange={(e) => {
                      setFilterCategory(e.target.value);
                      setPage(1);
                    }}
                  >
                    <option value="">All Categories</option>
                    {categories.map((c) => (
                      <option key={c._id} value={c._id}>
                        {c.categoryName}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-sm-4 col-md-3">
                  <label className="form-label mb-1 small">From</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={filterDateFrom}
                    max={filterDateTo || undefined}
                    onChange={(e) => {
                      setFilterDateFrom(e.target.value);
                      setPage(1);
                    }}
                  />
                </div>
                <div className="col-sm-4 col-md-3">
                  <label className="form-label mb-1 small">To</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={filterDateTo}
                    min={filterDateFrom || undefined}
                    onChange={(e) => {
                      setFilterDateTo(e.target.value);
                      setPage(1);
                    }}
                  />
                </div>
                <div className="col-auto">
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => {
                      setFilterCategory("");
                      setFilterDateFrom("");
                      setFilterDateTo("");
                      setPage(1);
                    }}
                  >
                    Reset
                  </button>
                </div>
              </div>
            </div>

            {/* Table */}
            <div className="card-body">
              <div className="table-responsive">
                <div className="dataTables_wrapper no-footer">
                  <div className="dataTables_length mb-3">
                    <label className="d-flex align-items-center gap-2">
                      Show
                      <Dropdown>
                        <Dropdown.Toggle as="div" className="search-drop-btn">
                          {sort}
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          {[10, 20, 50].map((n) => (
                            <Dropdown.Item
                              key={n}
                              onClick={() => {
                                setSort(n);
                                setPage(1);
                              }}
                            >
                              {n}
                            </Dropdown.Item>
                          ))}
                        </Dropdown.Menu>
                      </Dropdown>
                      entries
                    </label>
                  </div>

                  <table className="display dataTable no-footer w-100">
                    <thead
                      style={{
                        backgroundColor: "#f1f3f5",
                        textAlign: "center",
                      }}
                    >
                      <tr>
                        <th>Date</th>
                        <th>Type</th>
                        <th>Category</th>
                        <th>Amount (₹)</th>
                        <th>Balance (₹)</th>
                        <th>Posted By</th>
                        <th>Info</th>
                        <th>Description</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {showLoading && (
                        <tr>
                          <td colSpan={10} className="text-center py-4">
                            <span className="spinner-border spinner-border-sm me-2" />
                            Loading…
                          </td>
                        </tr>
                      )}
                      {!showLoading && expenses.length === 0 && (
                        <tr>
                          <td
                            colSpan={10}
                            className="text-center py-4 text-muted"
                          >
                            No entries found for the selected filters.
                          </td>
                        </tr>
                      )}
                      {expenses.map((row, idx) => (
                        <tr key={row._id || idx}>
                          <td>{formatDate(row.created)}</td>
                          <td>
                            <span
                              className={badgeClass(row.type)}
                              style={{
                                textTransform: "capitalize",
                                alignSelf: "center",
                                fontsize: 14,
                              }}
                            >
                              {row.type}
                            </span>
                          </td>
                          <td>{row.category?.name || "-"}</td>
                          <td className="fw-semibold">
                            {Number(row.amount).toLocaleString("en-IN")}
                          </td>
                          <td
                            className={
                              balanceMap[row._id] < 0
                                ? "text-danger fw-semibold"
                                : ""
                            }
                          >
                            {(balanceMap[row._id] ?? 0).toLocaleString("en-IN")}
                          </td>
                          <td
                            style={{
                              textTransform: "capitalize",
                            }}
                          >
                            {row.user?.name || "-"}
                          </td>
                          <td>
                            {row.isVehicleExpense ? (
                              <InfoBadge variant="vehicle" icon="fa-truck">
                                {row.vehicle?.name || "-"}
                              </InfoBadge>
                            ) : row.isContractorPayment ? (
                              <InfoBadge variant="contractor">
                                {row.contractor?.name || "-"}
                              </InfoBadge>
                            ) : row.isSupplierPayment ? (
                              <InfoBadge variant="supplier">
                                {row.supplier?.name || "-"}
                              </InfoBadge>
                            ) : row.isImprestAllotment ? (
                              <InfoBadge variant="imprest" icon="fa-user">
                                {row.imprestEmployee?.name || "-"}
                              </InfoBadge>
                            ) : row.isNetplusTransfer ? (
                              <InfoBadge variant="netplus" icon="fa-wifi">
                                Netplus
                              </InfoBadge>
                            ) : null}
                          </td>
                          <td className="text-muted small">
                            {row.desc || "—"}
                          </td>
                          <td>
                            <div className="d-flex align-items-center gap-1">
                              {hasFinanceWrite && (
                                <button
                                  type="button"
                                  className="btn btn-xs btn-outline-primary"
                                  onClick={() => handleEditClick(row)}
                                  title="Edit entry"
                                >
                                  <i className="fa fa-pencil" />
                                </button>
                              )}
                              {row.isVehicleExpense &&
                              isValidId(row.vehicleId) ? (
                                <DetailButton
                                  variant="info"
                                  icon="fa-truck"
                                  title="Vehicle detail"
                                  onClick={() =>
                                    navigate(
                                      `/payments/finance/vehicle-detail/${row.vehicleId}`,
                                    )
                                  }
                                >
                                  Detail
                                </DetailButton>
                              ) : row.isContractorPayment &&
                                isValidId(row.contractorId) ? (
                                <DetailButton
                                  variant="warning"
                                  icon="fa-user-circle"
                                  title="Contractor detail"
                                  onClick={() =>
                                    navigate(
                                      `/payments/finance/contractor-detail/${row.contractorId}`,
                                    )
                                  }
                                >
                                  Detail
                                </DetailButton>
                              ) : row.isSupplierPayment &&
                                isValidId(row.supplierId) ? (
                                <DetailButton
                                  variant="secondary"
                                  icon="fa-cubes"
                                  title="Supplier detail"
                                  onClick={() =>
                                    navigate(
                                      `/payments/finance/supplier-detail/${row.supplierId}`,
                                    )
                                  }
                                >
                                  Detail
                                </DetailButton>
                              ) : isValidId(row.categoryId) ? (
                                <DetailButton
                                  variant="success"
                                  icon="fa-bar-chart"
                                  title="Category detail"
                                  onClick={() =>
                                    navigate(
                                      `/payments/finance/category-detail/${row.categoryId}`,
                                    )
                                  }
                                >
                                  Detail
                                </DetailButton>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  <div className="d-sm-flex text-center justify-content-between align-items-center mt-3">
                    <div className="dataTables_info">
                      Showing {showingFrom} to {showingTo} of {total} entries
                    </div>
                    <div className="dataTables_paginate paging_simple_numbers">
                      <Link
                        className={`paginate_button previous ${page <= 1 ? "disabled" : ""}`}
                        to="#"
                        onClick={() => page > 1 && setPage(page - 1)}
                      >
                        Previous
                      </Link>
                      <span>
                        {pageNumbers.map((n) => (
                          <Link
                            key={n}
                            to="#"
                            className={`paginate_button ${page === n ? "current" : ""}`}
                            onClick={() => setPage(n)}
                          >
                            {n}
                          </Link>
                        ))}
                      </span>
                      <Link
                        className={`paginate_button next ${page >= totalPages ? "disabled" : ""}`}
                        to="#"
                        onClick={() => page < totalPages && setPage(page + 1)}
                      >
                        Next
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── 24-hour Lockout Warning Modal ──────────────────────────────────── */}
      <Modal
        show={lockModal.show}
        onHide={() => setLockModal({ show: false, record: null })}
        centered
      >
        <Modal.Header closeButton className="bg-warning-subtle">
          <Modal.Title>
            <i className="fa fa-lock me-2 text-warning" />
            Edit Window Expired
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2">
            This entry was posted on{" "}
            <strong>{formatDate(lockModal.record?.created)}</strong>, which is
            more than 24 hours ago.
          </p>
          <p className="mb-0 text-muted">
            Direct edits are locked after the 24-hour window. To modify this
            entry, please contact a <strong>Super Admin</strong> and provide the
            following details:
          </p>
          <ul className="mt-2 text-muted small">
            <li>
              Entry ID: <code>{lockModal.record?._id}</code>
            </li>
            <li>Amount: ₹{lockModal.record?.amount}</li>
            <li>Date: {formatDate(lockModal.record?.created)}</li>
          </ul>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setLockModal({ show: false, record: null })}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── Edit Entry Modal ──────────────────────────────────────────────── */}
      <Modal
        show={editModal.show}
        onHide={() => setEditModal({ show: false, record: null })}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit Finance Entry</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {hasFinanceWrite &&
            isEditWindowExpired(editModal.record?.created) && (
              <div className="alert alert-warning py-2 mb-3 small">
                <i className="fa fa-shield me-1" />
                You are editing a locked entry.
              </div>
            )}
          <form id="edit-expense-form" onSubmit={handleEditSubmit}>
            <div className="row">
              <div className="col-sm-6">
                <div className="form-group mb-3">
                  <label className="form-label">
                    Amount (₹) <span className="text-danger">*</span>
                  </label>
                  <input
                    name="amount"
                    type="number"
                    min="1"
                    className={`form-control ${editErrors.amount ? "is-invalid" : ""}`}
                    value={editForm.amount}
                    onChange={handleEditChange}
                  />
                  {editErrors.amount && (
                    <div className="invalid-feedback">{editErrors.amount}</div>
                  )}
                </div>
              </div>
              <div className="col-sm-6">
                <div className="form-group mb-3">
                  <label className="form-label">
                    Category <span className="text-danger">*</span>
                  </label>
                  <select
                    name="categoryId"
                    className={`form-control ${editErrors.categoryId ? "is-invalid" : ""}`}
                    value={editForm.categoryId}
                    onChange={handleEditChange}
                  >
                    <option value="">Select Category</option>
                    {categories.map((c) => (
                      <option key={c._id} value={c._id}>
                        {c.categoryName}
                      </option>
                    ))}
                  </select>
                  {editErrors.categoryId && (
                    <div className="invalid-feedback">
                      {editErrors.categoryId}
                    </div>
                  )}
                </div>
              </div>
              <div className="col-12">
                <div className="form-group mb-3">
                  <label className="form-label">Description</label>
                  <textarea
                    name="desc"
                    className="form-control"
                    rows={2}
                    value={editForm.desc}
                    onChange={handleEditChange}
                  />
                </div>
              </div>
              <div className="col-12">
                <div className="form-group mb-3">
                  <div className="form-check">
                    <input
                      id="edit-isVehicle"
                      name="isVehicle"
                      type="checkbox"
                      className="form-check-input"
                      checked={editForm.isVehicle || false}
                      onChange={handleEditChange}
                    />
                    <label
                      className="form-check-label"
                      htmlFor="edit-isVehicle"
                    >
                      Vehicle / Machine Expense
                    </label>
                  </div>
                </div>
              </div>
              {editForm.isVehicle && (
                <div className="col-sm-6">
                  <div className="form-group mb-3">
                    <label className="form-label">
                      Select Vehicle <span className="text-danger">*</span>
                    </label>
                    <select
                      name="vehicleId"
                      className={`form-control ${editErrors.vehicleId ? "is-invalid" : ""}`}
                      value={editForm.vehicleId}
                      onChange={handleEditChange}
                    >
                      <option value="">Select Vehicle</option>
                      {vehicles.map((v) => (
                        <option key={v._id || v.id} value={v._id || v.id}>
                          {v.vehicleMake
                            ? `${v.vehicleMake} — ${v.vehicleNumber}`
                            : v.vehicleNumber || v._id}
                        </option>
                      ))}
                    </select>
                    {editErrors.vehicleId && (
                      <div className="invalid-feedback">
                        {editErrors.vehicleId}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setEditModal({ show: false, record: null })}
            disabled={editLoading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            form="edit-expense-form"
            disabled={editLoading}
          >
            {editLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-1" />
                Saving…
              </>
            ) : (
              "Save Changes"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <AllotImprestModal
        show={showAllotModal}
        onClose={() => setShowAllotModal(false)}
        currentBalance={currentDaybookBalance}
      />
    </>
  );
};

export default ExpenseList;
